import {
  AlertCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  SettingsIcon,
  PlayIcon,
  BarChart3Icon,
} from 'lucide-react'
import { useState, useEffect } from 'react'

import { getDeploymentContract } from '../config/activeDeploymentContract'
import { Badge } from './badge'
import { Button } from './button'
import { Card, CardContent, CardHeader, CardTitle } from './card'

// A base da API vem do CONTRATO DE DEPLOY (#996), sem fallback: `|| 'http://localhost:8000'`
// num bundle de produção não é "cair no dev", é o card falar com um host que não existe —
// e o erro aparece como falha de rede, não como configuração faltando.
const API_BASE_URL = getDeploymentContract().apiBaseUrl

interface MetricsIntegrationStatus {
  cluster_id: string
  cluster_name: string
  integration_status: {
    configured: boolean
    enabled: boolean
    correctly_configured: boolean
    needs_setup: boolean
    working: boolean
    data_flowing: boolean
  }
  influxdb_bucket: {
    exists: boolean
    name: string
    measurement_count: number
    data_points_count: number
    data_age_hours?: number
  }
  proxmox_config: {
    metric_server_configured: boolean
    metric_server_enabled: boolean
    correctly_configured: boolean
    needs_setup: boolean
    validation_errors: string[]
    current_config: any
    expected_config: any
  }
  recommendations: string[]
}

interface SetupInstructions {
  web_interface: {
    steps: string[]
  }
  cli_command: string
  api_command: string
}

interface SetupResponse {
  success: boolean
  message: string
  details: {
    cluster_id: string
    cluster_name: string
    bucket: {
      success: boolean
      name: string
      retention_days: number
    }
    proxmox: {
      success: boolean
      message: string
      manual_setup_required: boolean
      configuration: {
        type: string
        server: string
        port: number
        protocol: string
        organization: string
        bucket: string
        token: string
      }
      instructions: SetupInstructions
    }
  }
}

interface MetricsIntegrationCardProps {
  clusterId: string
  onSetupComplete?: () => void
}

export function MetricsIntegrationCard({
  clusterId,
  onSetupComplete,
}: MetricsIntegrationCardProps) {
  const [status, setStatus] = useState<MetricsIntegrationStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [setupInstructions, setSetupInstructions] = useState<SetupResponse | null>(null)
  const [showInstructions, setShowInstructions] = useState(false)

  // Get auth token from localStorage
  const getAuthToken = () => {
    // Try multiple possible token keys
    const possibleKeys = ['auth_token', 'token', 'access_token']

    for (const key of possibleKeys) {
      const value = localStorage.getItem(key)
      if (value) return value
    }

    // Try Zustand store keys
    const storeKeys = Object.keys(localStorage).filter(
      (key) => key.includes('nebvla-auth') || key.includes('auth'),
    )

    for (const key of storeKeys) {
      try {
        const data = JSON.parse(localStorage.getItem(key) || '{}')
        const token = data?.state?.token || data?.token
        if (token) return token
      } catch (e) {
        // Continue to next key
      }
    }

    return null
  }

  // Fetch integration status
  const fetchStatus = async () => {
    try {
      setLoading(true)
      const token = getAuthToken()

      if (!token) {
        console.error('No auth token found')
        return
      }

      const response = await fetch(
        `${API_BASE_URL}/api/v1/metrics-config/clusters/${clusterId}/integration-status`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      )

      if (response.ok) {
        const data = await response.json()
        setStatus(data)
      } else {
        console.error('Failed to fetch metrics status:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('Failed to fetch metrics status:', error)
    } finally {
      setLoading(false)
    }
  }

  // Setup integration
  const setupIntegration = async () => {
    try {
      setActionLoading('setup')
      const token = getAuthToken()

      if (!token) {
        console.error('No auth token found')
        return
      }

      const response = await fetch(
        `${API_BASE_URL}/api/v1/metrics-config/clusters/${clusterId}/setup-integration`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            retention_days: 365,
            enable_integration: true,
          }),
        },
      )

      if (response.ok) {
        const result: SetupResponse = await response.json()
        if (result.success) {
          // Store setup instructions for display
          setSetupInstructions(result)
          setShowInstructions(true)

          // Refresh status after setup
          setTimeout(fetchStatus, 3000)
          onSetupComplete?.()
        }
      }
    } catch (error) {
      console.error('Failed to setup integration:', error)
    } finally {
      setActionLoading(null)
    }
  }

  // Test integration
  const testIntegration = async () => {
    try {
      setActionLoading('test')
      const token = getAuthToken()

      if (!token) {
        console.error('No auth token found')
        return
      }

      const response = await fetch(
        `${API_BASE_URL}/api/v1/metrics-config/clusters/${clusterId}/test-integration`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      )

      if (response.ok) {
        // Refresh status after test
        fetchStatus()
      }
    } catch (error) {
      console.error('Failed to test integration:', error)
    } finally {
      setActionLoading(null)
    }
  }

  useEffect(() => {
    fetchStatus()
  }, [clusterId])

  if (loading && !status) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2">
            <ClockIcon className="h-4 w-4 animate-spin" />
            <span>Verificando status das métricas...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!status) {
    return null
  }

  const getStatusBadge = () => {
    const { integration_status } = status

    if (integration_status.working && integration_status.data_flowing) {
      return (
        <Badge variant="default" className="bg-green-500 text-white">
          <CheckCircleIcon className="mr-1 h-3 w-3" />
          Funcionando
        </Badge>
      )
    }

    if (integration_status.correctly_configured && integration_status.enabled) {
      return (
        <Badge variant="secondary" className="bg-blue-500 text-white">
          <CheckCircleIcon className="mr-1 h-3 w-3" />
          Configurado Corretamente
        </Badge>
      )
    }

    if (integration_status.configured && integration_status.enabled) {
      return (
        <Badge variant="secondary" className="bg-yellow-500 text-white">
          <AlertCircleIcon className="mr-1 h-3 w-3" />
          Configuração Incorreta
        </Badge>
      )
    }

    if (integration_status.configured && !integration_status.enabled) {
      return (
        <Badge variant="secondary" className="bg-orange-500 text-white">
          <ClockIcon className="mr-1 h-3 w-3" />
          Desabilitado
        </Badge>
      )
    }

    return (
      <Badge variant="outline" className="bg-gray-500 text-white">
        <AlertCircleIcon className="mr-1 h-3 w-3" />
        Não configurado
      </Badge>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center space-x-2">
            <BarChart3Icon className="h-5 w-5" />
            <span>Integração de Métricas</span>
          </span>
          {getStatusBadge()}
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Status Information */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="font-medium text-gray-600">InfluxDB Bucket</div>
            <div className={status.influxdb_bucket.exists ? 'text-green-600' : 'text-gray-400'}>
              {status.influxdb_bucket.exists ? status.influxdb_bucket.name : 'Não criado'}
            </div>
          </div>

          <div>
            <div className="font-medium text-gray-600">Pontos de Dados</div>
            <div
              className={
                status.influxdb_bucket.data_points_count > 0 ? 'text-green-600' : 'text-gray-400'
              }
            >
              {status.influxdb_bucket.data_points_count.toLocaleString()}
            </div>
          </div>

          <div>
            <div className="font-medium text-gray-600">Measurements</div>
            <div
              className={
                status.influxdb_bucket.measurement_count > 0 ? 'text-green-600' : 'text-gray-400'
              }
            >
              {status.influxdb_bucket.measurement_count}
            </div>
          </div>

          <div>
            <div className="font-medium text-gray-600">Proxmox Server</div>
            <div
              className={
                status.proxmox_config.metric_server_enabled ? 'text-green-600' : 'text-gray-400'
              }
            >
              {status.proxmox_config.metric_server_enabled ? 'Ativo' : 'Inativo'}
            </div>
          </div>
        </div>

        {/* Data Freshness */}
        {status.influxdb_bucket.data_age_hours !== undefined && (
          <div className="text-sm">
            <div className="font-medium text-gray-600">Último Dado</div>
            <div
              className={
                status.influxdb_bucket.data_age_hours < 1 ? 'text-green-600' : 'text-yellow-600'
              }
            >
              {status.influxdb_bucket.data_age_hours < 1
                ? `${Math.round(status.influxdb_bucket.data_age_hours * 60)} minutos atrás`
                : `${status.influxdb_bucket.data_age_hours.toFixed(1)} horas atrás`}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {status.recommendations.length > 0 && (
          <div className="space-y-2">
            <div className="font-medium text-gray-600">Recomendações</div>
            <ul className="space-y-1 text-sm">
              {status.recommendations.map((rec, index) => (
                <li
                  key={index}
                  className={rec.includes('✅') ? 'text-green-600' : 'text-yellow-600'}
                >
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 border-t pt-4">
          {status.integration_status.needs_setup && (
            <Button
              onClick={setupIntegration}
              disabled={actionLoading === 'setup'}
              size="sm"
              className="bg-blue-500 hover:bg-blue-600"
            >
              {actionLoading === 'setup' ? (
                <>
                  <ClockIcon className="mr-1 h-3 w-3 animate-spin" />
                  Configurando...
                </>
              ) : (
                <>
                  <PlayIcon className="mr-1 h-3 w-3" />
                  {status.integration_status.configured ? 'Reconfigurar' : 'Configurar Agora'}
                </>
              )}
            </Button>
          )}

          {status.integration_status.configured && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={testIntegration}
                disabled={actionLoading === 'test'}
              >
                {actionLoading === 'test' ? (
                  <>
                    <ClockIcon className="mr-1 h-3 w-3 animate-spin" />
                    Testando...
                  </>
                ) : (
                  <>
                    <SettingsIcon className="mr-1 h-3 w-3" />
                    Testar
                  </>
                )}
              </Button>

              <Button variant="outline" size="sm" onClick={fetchStatus} disabled={loading}>
                <BarChart3Icon className="mr-1 h-3 w-3" />
                Atualizar
              </Button>
            </>
          )}
        </div>

        {/* Manual Setup Instructions */}
        {showInstructions && setupInstructions && (
          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-800">Instruções de Configuração Manual</h4>
              <Button variant="outline" size="sm" onClick={() => setShowInstructions(false)}>
                Fechar
              </Button>
            </div>

            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <div className="flex items-start space-x-2">
                <AlertCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">{setupInstructions.message}</p>
                  <p className="mt-1 text-xs text-yellow-700">
                    O bucket InfluxDB foi criado. Configure o Proxmox manualmente usando as
                    informações abaixo.
                  </p>
                </div>
              </div>
            </div>

            {/* Configuration Data */}
            <div className="rounded-lg bg-gray-50 p-4">
              <h5 className="mb-3 font-medium text-gray-800">Dados de Configuração:</h5>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Tipo:</span>
                  <span className="ml-2 font-mono">
                    {setupInstructions.details.proxmox.configuration.type}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Servidor:</span>
                  <span className="ml-2 font-mono">
                    {setupInstructions.details.proxmox.configuration.server}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Porta:</span>
                  <span className="ml-2 font-mono">
                    {setupInstructions.details.proxmox.configuration.port}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Protocolo:</span>
                  <span className="ml-2 font-mono">
                    {setupInstructions.details.proxmox.configuration.protocol}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Organização:</span>
                  <span className="ml-2 font-mono">
                    {setupInstructions.details.proxmox.configuration.organization}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Bucket:</span>
                  <span className="ml-2 font-mono text-xs">
                    {setupInstructions.details.proxmox.configuration.bucket}
                  </span>
                </div>
              </div>
              <div className="mt-3">
                <span className="text-gray-600">Token:</span>
                <div className="mt-1 rounded border bg-white p-2 font-mono text-xs break-all">
                  {setupInstructions.details.proxmox.configuration.token}
                </div>
              </div>
            </div>

            {/* Instructions Tabs */}
            <div className="space-y-4">
              <h5 className="font-medium text-gray-800">Métodos de Configuração:</h5>

              {/* Web Interface */}
              <div className="rounded-lg border p-4">
                <h6 className="mb-2 font-medium text-gray-700">🌐 Interface Web do Proxmox</h6>
                <ol className="space-y-1 text-sm text-gray-600">
                  {setupInstructions.details.proxmox.instructions.web_interface.steps.map(
                    (step, index) => (
                      <li key={index}>{step}</li>
                    ),
                  )}
                </ol>
              </div>

              {/* CLI Command */}
              <div className="rounded-lg border p-4">
                <h6 className="mb-2 font-medium text-gray-700">⌨️ Linha de Comando</h6>
                <div className="overflow-x-auto rounded bg-black p-3 font-mono text-xs text-green-400">
                  <pre>{setupInstructions.details.proxmox.instructions.cli_command}</pre>
                </div>
              </div>

              {/* API Command */}
              <div className="rounded-lg border p-4">
                <h6 className="mb-2 font-medium text-gray-700">🔗 API cURL</h6>
                <div className="overflow-x-auto rounded bg-black p-3 font-mono text-xs text-green-400">
                  <pre>{setupInstructions.details.proxmox.instructions.api_command}</pre>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start space-x-2">
                <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Próximos Passos:</p>
                  <ul className="mt-1 space-y-1 text-xs text-blue-700">
                    <li>• Configure o metric server no Proxmox usando um dos métodos acima</li>
                    <li>• Aguarde alguns minutos para os dados começarem a fluir</li>
                    <li>• Use o botão "Testar" para verificar se a integração está funcionando</li>
                    <li>• Use "Atualizar" para ver o status mais recente</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
